import React from 'react';
import { Text, Flex, Tooltip } from '@chakra-ui/react';
import { Table, Badge, ButtonIcon } from '@morressier/alembic';

import { IAuthorWithEmail } from '@publishing-engine/domain/models';
import { ContributionsBadge } from 'src/components/Submission/ContributionsBadge';
import AuthorDetailsModal from 'src/components/Submission/AuthorsTab/AuthorDetailsModal';
import Divider from 'src/components/Divider';
import RinggoldBadge from 'src/components/RinggoldBadge';
import { IPeerReviewManagementAuthorView } from '@publishing-engine/domain/views';
import { useSubmissionFields } from 'src/hooks/useSubmissionFields';
import {
  FormFieldAuthors,
  FormFieldContactAuthor,
  FormFieldCorrespondingAuthor,
  FormFieldSubmittingAuthor
} from '@publishing-engine/domain/types/article';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-light-svg-icons';
import {
  groupAuthorsByRoles,
  IAuthorRolesMap
} from 'src/utils/groupAuthorByRoles';

const AuthorsTab: React.FC<{}> = () => {
  const { getFieldState } = useSubmissionFields('review');

  const authorsField = getFieldState<FormFieldAuthors>('Authors');
  const submittingAuthorEmail =
    getFieldState<FormFieldSubmittingAuthor>('SubmittingAuthor')?.value!;
  const contactAuthorEmail =
    getFieldState<FormFieldContactAuthor>('ContactAuthor')?.value;
  const correspondingAuthorEmail = getFieldState<FormFieldCorrespondingAuthor>(
    'CorrespondingAuthor'
  )?.value;

  const roles: IAuthorRolesMap = {
    Submitting: submittingAuthorEmail,
    ...(contactAuthorEmail && {
      Contact: contactAuthorEmail
    }),
    ...(correspondingAuthorEmail && {
      Corresponding: correspondingAuthorEmail
    })
  };

  const [selectedAuthor, setSelectedAuthor] =
    React.useState<IPeerReviewManagementAuthorView | null>(null);

  const renderAuthorTableRow = (author: IPeerReviewManagementAuthorView) => {
    return (
      <Table.Row alignItems="start" _last={{ borderBottom: 'none' }}>
        <Table.RowCell
          flex={1}
          gap={2}
          padding={2}
          display="flex"
          flexDirection="column"
        >
          {author?.firstName || author?.lastName ? (
            <Text textStyle="actionLabel">
              {author?.firstName} {author?.lastName}
            </Text>
          ) : (
            <Text color="actionSecondaryCautionContent" textStyle="actionLabel">
              No Name Data
            </Text>
          )}
        </Table.RowCell>

        <Table.RowCell
          flex={1}
          gap={2}
          padding={2}
          display="flex"
          flexDirection="row"
          alignItems="flexStart"
        >
          {author?.ringgoldId && <RinggoldBadge />}
          {author?.affiliation ? (
            <Text textStyle="textBody">
              {author?.affiliation}
              {author.country && `, ${author.country}`}
            </Text>
          ) : (
            <Text color="actionSecondaryCautionContent" textStyle="textBody">
              No Affiliation Data
            </Text>
          )}
        </Table.RowCell>

        <Table.RowCell
          flex={1}
          gap={2}
          padding={2}
          display="flex"
          flexDirection="row"
        >
          <ContributionsBadge contributions={author.contributions} />
        </Table.RowCell>
      </Table.Row>
    );
  };

  const AuthorCard = ({
    author,
    roles
  }: {
    author: IPeerReviewManagementAuthorView;
    roles: string[];
  }) => {
    return (
      <>
        {/* Corresponding Author */}
        <Flex
          flexDirection="column"
          padding={4}
          gap={4}
          bgColor="surfacePrimaryFill"
        >
          {roles.length && (
            <>
              <Flex gap={4}>
                {roles?.map(role => (
                  <Badge variant="attention" size="context">
                    {role} Author
                  </Badge>
                ))}
              </Flex>
              {Divider}
            </>
          )}
          {/* Avatar */}
          <Flex gap={4}>
            <Flex flexDirection="column" flex={1}>
              <Text textStyle="headingItem">
                {author.firstName} {author.lastName}
              </Text>
              <Text textStyle="textBody">
                {author.affiliation}
                {author?.country && `, ${author?.country}`}
              </Text>
            </Flex>
          </Flex>
        </Flex>
      </>
    );
  };

  const renderAuthors = () =>
    groupAuthorsByRoles(roles, authorsField?.value).map(author => (
      <AuthorCard key={author.email} author={author} roles={author.roles} />
    ));

  return (
    <>
      {selectedAuthor && (
        <AuthorDetailsModal
          author={selectedAuthor as IAuthorWithEmail}
          onBackClick={() => setSelectedAuthor(null)}
        />
      )}

      <Flex gap={8} flexDirection="column">
        <Flex flexDirection="column" alignItems="flex-start" gap={2}>
          <Text textStyle="headingTitle">Authors</Text>
          <Text textStyle="textBody">
            Thoroughly review the information for all authors in this
            submission.
          </Text>
        </Flex>

        {/* Author Role */}
        <Text textStyle="headingGroup">Author Roles</Text>

        {renderAuthors()}

        {authorsField?.value && authorsField.value.length > 0 ? (
          <>
            {/* Authors List */}
            <Text textStyle="headingGroup">All Authors</Text>

            <Table>
              <Table.HeaderRow display="flex" alignItems="center">
                <Table.HeaderCell flex={1} padding={2}>
                  Author
                </Table.HeaderCell>
                <Table.HeaderCell flex={1} padding={2}>
                  Affiliation
                </Table.HeaderCell>
                <Table.HeaderCell
                  flex={1}
                  display={'flex'}
                  padding={2}
                  alignItems="center"
                >
                  <Text as={'span'}>CRediT Contributions</Text>
                  <Tooltip
                    hasArrow
                    shouldWrapChildren
                    placement="top"
                    label="Contributor Role Taxonomy (CRediT) Contributions"
                    borderRadius={4}
                    data-cy="contributions-info-tooltip"
                  >
                    <ButtonIcon
                      size="small"
                      ariaLabel="Back to workspace settings"
                      icon={<FontAwesomeIcon icon={faInfoCircle} />}
                      data-cy="contributions-info-icon"
                    />
                  </Tooltip>
                </Table.HeaderCell>
              </Table.HeaderRow>

              <Table.Body>
                {authorsField.value?.map((author, index) => (
                  <React.Fragment key={index}>
                    {renderAuthorTableRow(author)}
                  </React.Fragment>
                ))}
              </Table.Body>
            </Table>
          </>
        ) : null}
      </Flex>
    </>
  );
};

export default AuthorsTab;
